import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A genie's oil lamp: a plump golden teapot-belly with a curvy upswept spout,
// a domed lid with a round knob and a big looping handle, resting on a small
// crimson tasselled cushion. Built to promise a wish — a little gleam sphere
// at the spout tip breathes bright ("idle"), and three named smoke puffs curl
// out of the spout, rising and fading in a staggered loop ("smoke").
// Storybook batch: plump masses, oversized knob and tassels, warm saturated
// colours + cream trim.
//
// Frame: y = 0 is the ground, +Z is the front (the spout points forward).
// Roughly 0.5 m tall over a 1x1 tile.

const GOLD = "#e6ac3f";        // polished lamp gold
const GOLD_DARK = "#b07c22";   // gold shading (foot, handle, spout underside)
const VELVET = "#c23a52";      // crimson cushion
const CREAM = "#f6efe3";       // cream trim piping
const GLEAM = "#fff4c9";       // the wish-gleam at the spout tip
const SMOKE = "#cbd7ea";       // pale blue-grey magic smoke

// ── frame ────────────────────────────────────────────────────────────────────
const CUSHION_Y = 0.055;              // cushion centre height
const BODY_CY = CUSHION_Y + 0.05 + 0.13; // lamp belly centre (~0.24)
const TIP: [number, number, number] = [0, BODY_CY + 0.125, 0.305]; // spout mouth

// One corner tassel: a gold cord knot with a dangling bob.
function tassel(a: number): Part[] {
  const x = Math.sin(a) * 0.19;
  const z = Math.cos(a) * 0.19;
  return [
    { geometry: new THREE.SphereGeometry(0.02, 6, 5), color: GOLD,
      position: [x, CUSHION_Y, z] },
    { geometry: new THREE.ConeGeometry(0.018, 0.05, 6), color: GOLD_DARK,
      position: [x * 1.08, CUSHION_Y - 0.035, z * 1.08] },
  ];
}

// One smoke puff, given its own name so each rises on its own beat. Authored
// at a mid-rise pose so the static preview already reads as "smoking".
function puff(name: string, dx: number, lift: number, r: number): Part {
  return {
    geometry: new THREE.SphereGeometry(r, 8, 6), color: SMOKE,
    position: [TIP[0] + dx, TIP[1] + lift, TIP[2] + lift * 0.25], name,
  };
}

// A puff's life in one loop: swell out of the spout, curl up and drift, then
// shrink to nothing and wait under the tip for its next turn. `at` staggers
// the beat; the sideways sway gives the wisp its curl.
function puffTrack(target: string, at: number, dur: number, rise: number) {
  const t = (k: number) => (at + k * dur) % dur;
  const keys = [
    { k: 0.0, s: 0.1, y: 0, x: 0 },          // dormant at the spout mouth
    { k: 0.25, s: 0.9, y: rise * 0.25, x: 0.03 },  // swelled, curling right
    { k: 0.55, s: 1.15, y: rise * 0.65, x: -0.035 }, // curling back left
    { k: 0.85, s: 0.1, y: rise, x: 0.015 },  // faded out at the top
    { k: 1.0, s: 0.1, y: 0, x: 0 },          // sink home for the next beat
  ].map((p) => ({ t: t(p.k), s: p.s, y: p.y, x: p.x }))
    .sort((a, b) => a.t - b.t);
  return {
    target,
    pivot: TIP,
    scale: keys.map((p) => ({ t: p.t, value: p.s, ease: "sine" as const })),
    position: keys.map((p) => ({
      t: p.t, value: [p.x, p.y, p.y * 0.2] as [number, number, number],
      ease: "sine" as const,
    })),
  };
}

export const magicLamp = defineModel({
  id: "magic-lamp",
  batchId: "mess",
  name: "Magic lamp",
  tags: ["mess", "cute", "animated", "prop", "magic", "genie", "fantasy"],
  summary: "A golden genie lamp on a tasselled cushion, its spout curling with smoke.",
  description:
    "A plump toy-like genie oil lamp: a squashed golden belly with a curvy " +
    "upswept spout, a domed lid with an oversized round knob, a looping " +
    "handle, all resting on a crimson cushion with cream piping and gold " +
    "tassels. The idle animation breathes a warm gleam at the spout tip; the " +
    "smoke animation curls three named puffs up out of the spout in a " +
    "staggered rising loop.",
  author: "models",
  footprint: 0.32,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // Crimson cushion: a plump squashed sphere, cream piping, four tassels.
      { geometry: new THREE.SphereGeometry(0.21, 12, 8), color: VELVET,
        position: [0, CUSHION_Y, 0], scale: [1.05, 0.5, 1.05] },
      { geometry: new THREE.TorusGeometry(0.195, 0.018, 8, 18), color: CREAM,
        position: [0, CUSHION_Y, 0], rotation: [Math.PI / 2, 0, 0] },
      ...tassel(Math.PI / 4), ...tassel((Math.PI * 3) / 4),
      ...tassel((Math.PI * 5) / 4), ...tassel((Math.PI * 7) / 4),

      // Lamp foot: a chunky flared pedestal pressing into the cushion.
      { geometry: new THREE.CylinderGeometry(0.075, 0.11, 0.06, 10), color: GOLD_DARK,
        position: [0, CUSHION_Y + 0.055, 0] },
      // The belly: a plump squashed golden sphere.
      { geometry: new THREE.SphereGeometry(0.155, 14, 10), color: GOLD,
        position: [0, BODY_CY, 0], scale: [1.1, 0.78, 1] },
      // Cream belly band — the trim line that sells the storybook look.
      { geometry: new THREE.TorusGeometry(0.163, 0.014, 8, 20), color: CREAM,
        position: [0, BODY_CY, 0], rotation: [Math.PI / 2, 0, 0], scale: [1.05, 1.05, 1] },

      // Domed lid with an oversized round knob.
      { geometry: new THREE.CylinderGeometry(0.05, 0.085, 0.06, 10), color: GOLD,
        position: [0, BODY_CY + 0.115, 0] },
      { geometry: new THREE.SphereGeometry(0.042, 8, 6), color: GOLD_DARK,
        position: [0, BODY_CY + 0.165, 0] },

      // The curvy spout: two overlapping segments sweeping up and forward from
      // the belly to the mouth, capped with a little flared tip ring.
      { geometry: new THREE.CylinderGeometry(0.036, 0.062, 0.15, 8), color: GOLD,
        position: [0, BODY_CY + 0.005, 0.185], rotation: [1.1, 0, 0] },
      { geometry: new THREE.CylinderGeometry(0.027, 0.038, 0.12, 8), color: GOLD,
        position: [0, BODY_CY + 0.072, 0.265], rotation: [0.6, 0, 0] },
      { geometry: new THREE.CylinderGeometry(0.038, 0.028, 0.035, 8), color: GOLD_DARK,
        position: [TIP[0], TIP[1] - 0.008, TIP[2] - 0.006], rotation: [0.6, 0, 0] },

      // The looping handle at the back, standing clear of the belly.
      { geometry: new THREE.TorusGeometry(0.068, 0.018, 8, 14), color: GOLD_DARK,
        position: [0, BODY_CY + 0.075, -0.175], rotation: [0, Math.PI / 2, 0] },

      // The wish-gleam at the spout mouth, named to breathe in the idle.
      { geometry: new THREE.SphereGeometry(0.032, 8, 6), color: GLEAM,
        position: TIP, name: "gleam" },

      // Three smoke puffs caught mid-rise (staggered for a lively still).
      puff("puff-a", 0.015, 0.07, 0.038),
      puff("puff-b", -0.025, 0.15, 0.03),
      puff("puff-c", 0.02, 0.23, 0.024),
    ];
  },
  animations: [
    // 1. Idle — the gleam breathes: a soft warm pulse at the spout mouth, as
    //    if the lamp is daydreaming about its next wish.
    {
      id: "idle",
      label: "Gleam",
      duration: 2.4,
      loop: true,
      tracks: [
        {
          target: "gleam", pivot: TIP,
          scale: [
            { t: 0, value: 0.7, ease: "sine" },
            { t: 1.2, value: 1.35, ease: "sine" },
            { t: 2.4, value: 0.7, ease: "sine" },
          ],
        },
        // The puffs stay tucked small at the mouth while the lamp rests.
        {
          target: "puff-a", pivot: TIP,
          scale: [{ t: 0, value: 0.35 }, { t: 2.4, value: 0.35 }],
          position: [{ t: 0, value: [0, -0.04, 0] }, { t: 2.4, value: [0, -0.04, 0] }],
        },
        {
          target: "puff-b", pivot: TIP,
          scale: [{ t: 0, value: 0.3 }, { t: 2.4, value: 0.3 }],
          position: [{ t: 0, value: [0, -0.12, -0.04] }, { t: 2.4, value: [0, -0.12, -0.04] }],
        },
        {
          target: "puff-c", pivot: TIP,
          scale: [{ t: 0, value: 0.25 }, { t: 2.4, value: 0.25 }],
          position: [{ t: 0, value: [0, -0.2, -0.05] }, { t: 2.4, value: [0, -0.2, -0.05] }],
        },
      ],
    },
    // 2. Smoke — the signature move: three puffs take turns curling out of the
    //    spout, swelling, drifting up and fading, while the gleam flares.
    {
      id: "smoke",
      label: "Smoke wisp",
      duration: 3.3,
      loop: true,
      tracks: [
        puffTrack("puff-a", 0, 3.3, 0.24),
        puffTrack("puff-b", 3.3 * 0.36, 3.3, 0.21),
        puffTrack("puff-c", 3.3 * 0.7, 3.3, 0.18),
        {
          target: "gleam", pivot: TIP,
          scale: [
            { t: 0, value: 1.0, ease: "sine" },
            { t: 1.65, value: 1.5, ease: "sine" },
            { t: 3.3, value: 1.0, ease: "sine" },
          ],
        },
      ],
    },
  ],
});
